/**
 * Client POs — purchase orders a client issues to LUX Construction (income).
 * Inbox-style list (New → Assigned → Closed) with a side-by-side review dialog:
 * PO document left, AI-read fields + project assignment right.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ClipboardList, Loader2, Upload, Download, Search, FileText, Mail, Sparkles, AlertTriangle, X } from 'lucide-react';
import { ClientPoDialog } from '../components/client-pos/ClientPoDialog';
import { LedgerTabs } from '../components/LedgerTabs';

const TABS = [{ key: 'new', label: 'New' }, { key: 'assigned', label: 'Assigned' }, { key: 'closed', label: 'Closed' }, { key: 'all', label: 'All' }];
const gbp = (n) => (n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`);
const fmtD = (s) => { try { return s ? format(parseISO(s), 'dd MMM yyyy') : '—'; } catch { return s || '—'; } };

function PoRow({ po, onOpen, onDismiss }) {
  const reading = po.extraction_status === 'pending' || po.extraction_status === 'running';
  // A pre-filled project on a still-"new" PO is only the AI's suggestion until an admin saves it.
  const unconfirmed = po.status === 'new' && po.suggestion && po.project_id === po.suggestion.project_id;
  const suggested = po.suggestion && (!po.project_ref || unconfirmed);
  return (
    <button type="button" onClick={() => onOpen(po)} className="w-full text-left bg-[#2A3240] border border-white/10 rounded-lg px-4 py-3 hover:bg-white/5 transition-colors flex items-center gap-4" data-testid={`client-po-row-${po.id}`}>
      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
        {reading ? <Loader2 className="w-4 h-4 animate-spin text-[#F5A623]" /> : <FileText className="w-4 h-4 text-gray-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-gray-100 truncate" data-testid={`client-po-number-${po.id}`}>{po.po_number || po.original_filename || 'Purchase order'}</span>
          {po.source === 'email' && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-200 border border-sky-400/40"><Mail className="w-3 h-3" /> Email</span>}
          {po.reverse_charge && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-gray-300 border border-white/15">Reverse charge</span>}
          {po.extraction_status === 'failed' && <span className="inline-flex items-center gap-1 text-[10px] text-amber-300"><AlertTriangle className="w-3 h-3" /> AI read failed</span>}
        </div>
        <div className="text-xs text-gray-400 truncate">{po.client_name || 'Client not read yet'}{po.site_address ? ` · ${po.site_address}` : ''}{po.site_postcode && !(po.site_address || '').toUpperCase().includes(po.site_postcode.toUpperCase()) ? ` ${po.site_postcode}` : ''}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-semibold tabular-nums text-gray-100" data-testid={`client-po-value-${po.id}`}>{gbp(po.value_pounds)}</div>
        <div className="text-[11px] text-gray-500">{fmtD(po.po_date)}</div>
      </div>
      <div className="w-48 shrink-0 text-right">
        {suggested ? (
          <span className="inline-flex items-center gap-1 text-xs text-[#F5A623] max-w-full" title={`Suggested — ${po.suggestion.reason}`} data-testid={`client-po-suggested-${po.id}`}>
            <Sparkles className="w-3 h-3 shrink-0" /> <span className="truncate">{po.suggestion.project_ref}</span>
            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onDismiss(po); }} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onDismiss(po); } }} className="ml-0.5 rounded-full p-0.5 text-gray-400 hover:text-white hover:bg-white/10" title="Not this project — dismiss the suggestion" data-testid={`client-po-dismiss-${po.id}`}><X className="w-3 h-3" /></span>
          </span>
        ) : po.project_ref ? (
          <span className="text-xs text-emerald-300 truncate block" data-testid={`client-po-project-${po.id}`}>{po.project_ref}</span>
        ) : po.suggestion_dismissed ? (
          <span className="text-xs text-gray-500" title={`Suggestion ${po.suggestion_dismissed.project_ref} dismissed`} data-testid={`client-po-awaiting-${po.id}`}>Awaiting confirmation</span>
        ) : (
          <span className="text-xs text-gray-500">No project yet</span>
        )}
      </div>
    </button>
  );
}

export default function ClientPOs() {
  const { api, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('new');
  const [q, setQ] = useState('');
  const [data, setData] = useState({ items: [], counts: {}, total_pounds: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { if (user && !isAdmin) navigate('/projects'); }, [user, isAdmin, navigate]);
  useEffect(() => {
    const id = searchParams.get('open');
    if (id) { setOpenId(id); setTab('all'); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = { status: tab };
      if (q.trim()) params.q = q.trim();
      const r = await api.get('/client-pos', { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load client POs'); }
    finally { setLoading(false); }
  }, [api, tab, q]);
  useEffect(() => { load(); }, [load]);
  const reading = useMemo(() => data.items.some(p => p.extraction_status === 'pending' || p.extraction_status === 'running'), [data.items]);
  useEffect(() => {
    if (!reading) return undefined;
    const t = setInterval(() => load(true), 3000);
    return () => clearInterval(t);
  }, [reading, load]);

  const dismiss = async (po) => {
    try { await api.post(`/client-pos/${po.id}/suggestion/dismiss`); toast.success(`Suggestion ${po.suggestion?.project_ref || ''} dismissed — waiting on confirmation`); load(true); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not dismiss the suggestion'); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      [...files].forEach(f => fd.append('files', f));
      const r = await api.post('/client-pos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.items.length} PO${r.data.items.length === 1 ? '' : 's'} added — reading…`);
      setTab('new');
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get('/client-pos/export.csv', { params: { status: tab }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = 'client-pos.csv'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="min-h-screen bg-[#1E2530] text-white p-6" data-testid="client-pos-page">
      <div className="max-w-6xl mx-auto">
        <LedgerTabs className="mb-4" />
        <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2"><ClipboardList className="w-7 h-7 text-[#F5A623]" /> Client POs</h1>
            <p className="text-sm text-gray-400 mt-1">Purchase orders clients send to LUX Construction. Email them to the PO address or upload — the AI reads the order and suggests the project from the site address.</p>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={e => upload(e.target.files)} data-testid="client-po-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#f7b84a]" data-testid="client-po-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload PO
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="client-po-export-csv"><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/cost-inbox/email-in')} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="client-po-email-in"><Mail className="w-4 h-4 mr-1.5" /> Email-in</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {TABS.map(t => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} data-testid={`client-po-tab-${t.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === t.key ? 'bg-[#F5A623] text-[#1E2530] border-[#F5A623] font-semibold' : 'border-white/15 text-gray-300 hover:bg-white/10'}`}>
              {t.label}{t.key !== 'all' && data.counts?.[t.key] != null ? ` (${data.counts[t.key]})` : ''}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-500" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="PO number, client, address, job…" className="pl-8 h-9 w-72 bg-[#2A3240] border-white/15 text-white text-sm" data-testid="client-po-search" />
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400 mb-2 px-1">
          <span data-testid="client-po-count">{data.items.length} PO{data.items.length === 1 ? '' : 's'}</span>
          <span>Total ex VAT <b className="text-gray-100 tabular-nums" data-testid="client-po-total">{gbp(data.total_pounds)}</b></span>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="bg-[#2A3240] border border-white/10 rounded-lg p-10 text-center text-gray-500" data-testid="client-po-empty">
            No {tab === 'all' ? '' : tab} client POs{q ? ' match your search' : ''}.
          </div>
        ) : (
          <div className="space-y-2" data-testid="client-po-list">
            {data.items.map(po => <PoRow key={po.id} po={po} onOpen={(p) => setOpenId(p.id)} onDismiss={dismiss} />)}
          </div>
        )}
      </div>

      {openId && <ClientPoDialog api={api} poId={openId} onClose={() => setOpenId(null)} onChanged={() => load(true)} />}
    </div>
  );
}
